// useActiveJobs — fetches the current user's active jobs once and caches them.
// Used by the wrong-asset guard banner on asset modals.
import { useEffect, useState } from 'react';
import { getApiEndpoint } from '../config/api';
import { getAuthHeader } from '../utils/auth';

let cachedJobs = null;
let cachedAt = 0;
const TTL_MS = 60 * 1000; // 60s — short enough that freshly accepted jobs appear quickly.

export const ASSET_TYPE_TO_AREA = {
  anchor_point: 'Roof Safety',
  abseil_system: 'Roof Safety',
  cradle: 'Roof Safety',
  ladder: 'Roof Safety',
  handrail: 'Roof Safety',
  static_line: 'Roof Safety',
  walkway: 'Roof Safety',
  fixed_equipment: 'Equipment Safety',
};

export function evaluateAssetMismatch({ propertyId, assetType, activeJobs }) {
  if (!Array.isArray(activeJobs) || activeJobs.length === 0) {
    return { mismatch: false, reason: null, hasActiveJob: false };
  }
  const expectedArea = ASSET_TYPE_TO_AREA[assetType] || null;
  const matching = activeJobs.find(j => {
    if (Number(j.property_id) !== Number(propertyId)) return false;
    if (expectedArea && j.area && expectedArea.toLowerCase() !== j.area.toLowerCase()) return false;
    return true;
  });
  if (matching) {
    return { mismatch: false, reason: null, hasActiveJob: true, matchingJob: matching };
  }
  const j = activeJobs[0];
  if (Number(j.property_id) !== Number(propertyId)) {
    return {
      mismatch: true,
      reason: `Your active job is at ${j.property_name || `property #${j.property_id}`}, not this property.`,
      hasActiveJob: true,
    };
  }
  if (expectedArea && j.area && expectedArea.toLowerCase() !== j.area.toLowerCase()) {
    return {
      mismatch: true,
      reason: `Your active job is "${j.area}". This asset belongs to "${expectedArea}".`,
      hasActiveJob: true,
    };
  }
  return {
    mismatch: true,
    reason: 'This asset isn\'t on any of your active jobs.',
    hasActiveJob: true,
  };
}

export default function useActiveJobs() {
  const [jobs, setJobs] = useState(cachedJobs || []);
  const [loading, setLoading] = useState(!cachedJobs);

  useEffect(() => {
    const fresh = cachedJobs && (Date.now() - cachedAt) < TTL_MS;
    if (fresh) { setJobs(cachedJobs); setLoading(false); return; }

    let cancelled = false;
    setLoading(true);
    fetch(getApiEndpoint('/api/jobs/active-mine/all'), { headers: getAuthHeader() })
      .then(r => (r.ok ? r.json() : { jobs: [] }))
      .then(data => {
        if (cancelled) return;
        const list = Array.isArray(data?.jobs) ? data.jobs : [];
        cachedJobs = list;
        cachedAt = Date.now();
        setJobs(list);
      })
      .catch(() => { if (!cancelled) setJobs([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return { jobs, loading };
}
